import React, { useRef, useState } from 'react';
import '../../scss/components/FileDropzone.scss';

export interface UploadedFile {
    filename: string,
    payload: string,
}

interface FileDropzoneProps {
    name:string,
    label?:string,
    files:Array<UploadedFile>,
    onChange:(files:Array<UploadedFile>) => void,
    accept?:string,
    error?:string,
    maxFiles?:number,
    maxSizeKb?:number,
}

const readFileAsBase64 = (file:File):Promise<UploadedFile> => {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const result = reader.result;
            resolve({
                filename: file.name,
                payload: result ? String(result).split(',')[1] : '',
            });
        });
        reader.readAsDataURL(file);
    });
}

const FileDropzone = ({
    name,
    label,
    files,
    onChange,
    accept,
    error,
    maxFiles = 5,
    maxSizeKb = 2048,
}:FileDropzoneProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [localError, setLocalError] = useState('');

    const handleFiles = async (fileList:FileList) => {
        setLocalError('');
        const incoming = Array.from(fileList);
        const accepted:Array<File> = [];
        const messages:Array<string> = [];
        const remainingSlots = maxFiles - files.length;

        for (const file of incoming) {
            if (accepted.length >= remainingSlots) {
                messages.push(`Es sind maximal ${maxFiles} Dateien erlaubt.`);
                break;
            }
            if (file.size > maxSizeKb * 1024) {
                messages.push(`„${file.name}“ überschreitet die maximale Dateigröße von ${maxSizeKb} KB.`);
                continue;
            }
            accepted.push(file);
        }

        const converted = await Promise.all(accepted.map(readFileAsBase64));
        if (converted.length > 0) {
            onChange([...files, ...converted]);
        }
        if (messages.length > 0) {
            setLocalError(messages.join(' '));
        }
    }

    const removeFile = (index:number) => {
        onChange(files.filter((_, i) => i !== index));
        setLocalError('');
    }

    const handleDrop = (event:React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            handleFiles(event.dataTransfer.files);
        }
    }

    return (
        <div className='form-group file-dropzone'>
            { label && label.length > 0 && (
                <label htmlFor={name}>{label}</label>
            ) }
            <div
                className={`dropzone${isDragActive ? ' drag-active' : ''}${error ? ' invalid' : ''}`}
                role='button'
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
                onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragActive(false); }}
                onDrop={handleDrop}
            >
                <span className='dropzone-icon'></span>
                <span>Dateien hierher ziehen oder <span className='dropzone-link'>auswählen</span>.<br />PDF oder Bilddateien werden empfohlen.</span>
                <span className='dropzone-hint'>Max. {maxFiles} Dateien, je max. {maxSizeKb} KB</span>
                <input
                    ref={inputRef}
                    type='file'
                    name={name}
                    id={name}
                    multiple
                    accept={accept}
                    onChange={(e) => { if (e.target.files) { handleFiles(e.target.files); } e.target.value = ''; }}
                />
            </div>
            { files.length > 0 && (
                <ul className='dropzone-files'>
                    { files.map((file, index) => (
                        <li key={index}>
                            <span className='dropzone-file-name'>{file.filename}</span>
                            <button type='button' className='dropzone-file-remove' aria-label={`„${file.filename}“ entfernen`} onClick={() => removeFile(index)}>×</button>
                        </li>
                    )) }
                </ul>
            ) }
            { (localError || error) && (
                <span className='form-error' role='alert'>{ localError || error }</span>
            ) }
        </div>
    )
}

export default FileDropzone;
